import { useBillingCurrency } from "$global-providers/billing-provider/provider";
import {
  CURRENCY_BASE_DEFAULT,
  DECIMALS_PLACES_DEFAULT,
} from "$routes/_lib/constants";
import { Decimal } from "decimal.js-light";
import { curry } from "ramda";
import { useCallback, useMemo } from "react";

export const penceToPounds = curry((currency, amountInPence) => {
  if (!amountInPence && amountInPence !== 0) {
    return undefined;
  }

  const currencyDecimals = currency?.decimal ?? DECIMALS_PLACES_DEFAULT;
  const amountInPenceDecimal = new Decimal(amountInPence);
  return amountInPenceDecimal
    .div(CURRENCY_BASE_DEFAULT ** currencyDecimals)
    .toNumber();
});

export const poundsToPence = curry((currency, amountInPounds) => {
  if (!amountInPounds && amountInPounds !== 0) {
    return undefined;
  }

  const currencyDecimals = currency?.decimal ?? DECIMALS_PLACES_DEFAULT;
  const amountInPoundsDecimal = new Decimal(amountInPounds);
  return amountInPoundsDecimal
    .mul(CURRENCY_BASE_DEFAULT ** currencyDecimals)
    .toInteger()
    .toNumber();
});

/**
 * Will calculate and output a string compatible with HTML <input step />
 * attribute based on currency decimals settings. An example output would be
 * `0.01` with the default arguments for a currency that supports 2 decimals
 *
 * @param {*} currency Currency details object as returned by useBillingCurrency()
 */
export function inputStep(currency) {
  const showDecimals = !currency.format.compact ?? true;
  const decimalPlaces = currency.decimals ?? DECIMALS_PLACES_DEFAULT;

  return showDecimals ? `${10 ** -decimalPlaces}` : "1";
}

/**
 * Use these factory functions when you have to call it
 * multiple times within the same component. This way you can
 * save on calls to retrieve billing currency.
 */
export function useConvertFromPenceFactory() {
  const currency = useBillingCurrency();
  return useCallback(
    (amountInPence) => penceToPounds(currency, amountInPence),
    [currency],
  );
}

export function useConvertFromPoundsFactory() {
  const currency = useBillingCurrency();
  return useCallback(
    (amountInPounds) => poundsToPence(currency, amountInPounds),
    [currency],
  );
}

/**
 * A hook to convert money amounts from pence to pounds
 * @param {Int} amountInPence
 */
export function useConvertFromPence(amountInPence) {
  const currency = useBillingCurrency();
  return useMemo(
    () => penceToPounds(currency, amountInPence),
    [amountInPence, currency],
  );
}

/**
 * A hook to convert money amounts from pounds to pence
 * @param {Int} amountInPounds
 */
export function useConvertFromPounds(amountInPounds) {
  const currency = useBillingCurrency();
  return useMemo(
    () => poundsToPence(currency, amountInPounds),
    [amountInPounds, currency],
  );
}
